import { useState, useEffect } from 'react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CONTENT_TYPES = ['caption', 'hashtags', 'blog_outline', 'ad_copy', 'short_script'];
const PLATFORMS = ['instagram', 'twitter', 'facebook', 'linkedin', 'tiktok', 'blog', 'general'];

export default function PromptsPage() {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', content_type: 'caption', platform: 'general', template: '', is_global: false });

  const load = () => api.get('/prompts').then((r) => { setPrompts(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', content_type: 'caption', platform: 'general', template: '', is_global: false });
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, content_type: p.content_type, platform: p.platform || 'general', template: p.template, is_global: p.is_global });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/prompts/${editingId}`, form);
        toast.success('Prompt updated');
      } else {
        await api.post('/prompts', form);
        toast.success('Prompt created');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prompt template?')) return;
    try {
      await api.delete(`/prompts/${id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Loading prompts...</div>;

  return (
    <div data-testid="prompts-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Prompt Şablonları</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">{prompts.length} şablon mevcut</p>
        </div>
        <Button onClick={openCreate} data-testid="create-prompt-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] transition-transform duration-100">
          <Plus size={16} className="mr-2" /> Yeni Şablon
        </Button>
      </div>

      <Card className="bg-[#141414] border-[#262626]">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-[#A3A3A3] font-ibm">Ad</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Tür</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Platform</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm">Kapsam</TableHead>
              <TableHead className="text-[#A3A3A3] font-ibm text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prompts.map((p) => (
              <TableRow key={p.id} className="border-[#262626] hover:bg-white/5 transition-colors duration-200" data-testid={`prompt-row-${p.id}`}>
                <TableCell className="text-white font-ibm font-medium">{p.name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 capitalize">{p.content_type?.replace('_', ' ')}</Badge>
                </TableCell>
                <TableCell className="text-[#A3A3A3] font-ibm capitalize">{p.platform || 'general'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.is_global ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-green-500/10 text-green-400 border-green-500/20'}>
                    {p.is_global ? 'Global' : 'Kiracı'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-prompt-${p.id}`} className="text-[#A3A3A3] hover:text-white h-8 w-8 p-0">
                      <Edit size={14} />
                    </Button>
                    {(user?.role === 'SUPER_ADMIN' || user?.role === 'TENANT_ADMIN') && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-prompt-${p.id}`} className="text-[#A3A3A3] hover:text-red-400 h-8 w-8 p-0">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">{editingId ? 'Düzenle' : 'Oluştur'} Prompt Şablonu</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Ad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="prompt-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">İçerik Türü</Label>
                <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="prompt-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {CONTENT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="prompt-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Şablon</Label>
              <Textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[180px] font-jetbrains text-sm" data-testid="prompt-template-input" placeholder="Use {brand_name}, {industry}, {tone}, {goal}, {language}, {platform}, {target_audience}, {cta_style}, {custom_instructions}" />
            </div>
            {user?.role === 'SUPER_ADMIN' && (
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })} id="is_global" data-testid="prompt-global-checkbox" className="rounded border-[#262626] bg-[#0A0A0A]" />
                <Label htmlFor="is_global" className="text-[#A3A3A3] font-ibm text-sm cursor-pointer">Global şablon (tüm kiracılara açık)</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleSave} data-testid="save-prompt-btn" className="bg-white text-black hover:bg-[#E5E5E5]">{editingId ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
